
import React, { useState, useEffect } from "react";
import "../styles/HealthcarePage.css";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import SEO from "../components/SEO";

import * as productService from "../_services/products.service";

export default function MagyneMedicPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState(location.state?.product || null);

  const params = new URLSearchParams(location.search);

  const proceedTo = params.get("proceedTo") || "register";
  const tenantId = params.get("tenantId") || null;

  const productId =
    params.get("productId") ||
    "35b9d149-23b3-434a-9d0f-17d7d55c3806";

  const handleNavigate = () => {
    if (!product) {
      toast.error("Product information is unavailable.");
      return;
    }

    const status = product.status?.toUpperCase();

    if (status !== "ACTIVE") {
      switch (status) {
        case "PLANNED":
          toast("Magyne Medic is planned and is not yet available.", {
            icon: "🚀",
          });
          break;

        case "DEVELOPMENT":
          toast("Magyne Medic is Coming Soon.", {
            icon: "🛠️",
          });
          break;

        default:
          toast.error("This product is currently unavailable.");
      }

      return;
    }

    navigate(
      `/pricing/?productId=${product.productId}&proceedTo=${proceedTo}&tenantId=${tenantId}`,
      {
        state: { product },
      }
    );
  };

  useEffect(() => {
    const fetchProduct = async () => {
      if (!product && productId) {
        setLoading(true);

        try {
          const response = await productService.get({
            productId,
            status: "all"
          });

          if (!response.data?.length) {
            setProduct(null);
            return;
          }

          setProduct(response.data[0]);
        } catch (error) {
          toast.error(
            error?.message || "Failed to fetch product details."
          );
          setProduct(null);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchProduct();
  }, [product, productId]);

  const getStatusMessage = () => {
    if (!product?.status) return null;

    switch (product.status.toUpperCase()) {
      case "PLANNED":
        return "Magyne Medic is planned and will be available soon.";

      case "DEVELOPMENT":
        return "Magyne Medic is Coming Soon.";

      case "ACTIVE":
        return null;

      default:
        return "Magyne Medic is currently unavailable.";
    }
  };

  const isActive = product?.status?.toUpperCase() === "ACTIVE";

  const features = [
    {
      name: "Patient Management",
      details: "Manage patient records, appointments, and medical history efficiently.",
      benefits: "Improved patient experience, accurate record-keeping, and faster access to data."
    },
    {
      name: "Billing & Invoicing",
      details: "Automated billing for consultations, treatments, and medicines.",
      benefits: "Reduces errors and speeds up revenue collection."
    },
    {
      name: "Staff Management",
      details: "Manage doctors, nurses, and administrative staff schedules.",
      benefits: "Optimized staff allocation and smooth operations."
    },
    {
      name: "Inventory Management",
      details: "Track medical supplies, drugs, and equipment stock levels.",
      benefits: "Minimizes shortages and wastage."
    },
    {
      name: "Reports & Analytics",
      details: "Generate reports on hospital operations, finances, and patient trends.",
      benefits: "Informed decision-making and improved efficiency."
    }
  ];

  return (
    <>
      <SEO
        title="Magyne Medic | Healthcare Management Software"
        description="Magyne Medic is healthcare management software designed to help hospitals, clinics and healthcare organizations manage patients, administration and healthcare operations."
        path="/solutions/medic"
      />

      <div className="healthcare-page">

        {loading ? (
          <div>
            <div className="spinner"></div>

            <p className="text-center">
              Loading product.
              <br />
              Please wait...
            </p>
          </div>
        ) : !loading && product ? (
          <>
            <section
              className="education-hero"
              style={{
                backgroundImage: `url(/images/healthcare.jpg)`,
              }}
            >
              <div className="education-hero-overlay">

                <h1>
                  Magyne Medic <br/> Healthcare Software
                </h1>

                <p>
                  Magyne Medic helps hospitals, clinics and healthcare
                  organizations manage patients, clinical administration,
                  staff, billing and day-to-day healthcare operations
                  from one platform.
                </p>

                {!isActive && (
                  <div className="product-status-message">
                    {getStatusMessage()}
                  </div>
                )}

                <button
                  className="cta-btn"
                  onClick={handleNavigate}
                  disabled={!isActive}
                  title={
                    !isActive
                      ? getStatusMessage()
                      : "Choose a Magyne Medic package"
                  }
                >
                  {product.status?.toUpperCase() === "PLANNED"
                    ? "Coming Soon"
                    : product.status?.toUpperCase() === "DEVELOPMENT"
                    ? "Coming Soon"
                    : "Choose a Package"}
                </button>

              </div>
            </section>

            <section className="healthcare-features">
              <h2>Key Features</h2>
              <div className="features-grid">
                {features.map((feature, idx) => (
                  <div key={idx} className="feature-card">
                    <h3>{feature.name}</h3>
                    <p className="details">{feature.details}</p>
                    <p className="benefits">{feature.benefits}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* CTA */}
            <section className="education-cta">

              <h2>
                Modernize Your Healthcare Organization
              </h2>

              <p>
                Empower your hospital, clinic or healthcare organization
                with Magyne Medic and simplify administration through
                modern digital tools.
              </p>

              <button
                className="cta-btn"
                onClick={handleNavigate}
                disabled={!isActive}
              >
                {product.status?.toUpperCase() === "PLANNED"
                  ? "Coming Soon"
                  : product.status?.toUpperCase() === "DEVELOPMENT"
                  ? "Coming Soon"
                  : "Choose a Package"}
              </button>

            </section>
          </>
        ) : !loading && !product && (
          /* Invalid Product */
          <div className="login-card mt-lg-5 mb-lg-5">

            <div className="logo-container">

              <h3 className="page-title mb-2">
                Invalid product.
              </h3>

              <p>
                The requested product could not be found.
                Please select a valid Magyne solution.
              </p>

              <p className="register-text">
                Explore our products{" "}
                <a href="/products">
                  See products
                </a>
              </p>

            </div>

          </div>
        )}

      </div>
    </>
  );
}
